import { tr } from '@/contexts/LanguageContext';
// mediaDestinations — matriz rol → destinos disponibles + writers a cada store.
//
// Único punto donde se decide:
//   1) Qué destinos puede subir un rol (FEED, PERFIL, PORTAFOLIO, BANNER, etc).
//   2) Cómo persistir cada uno (a qué localStorage store y bajo qué shape).
//
// Cada destino expone:
//   { id, label, icon, requiresApproval, mediaAccept, write({user, media, fields}) }

const event = (name) => window.dispatchEvent(new Event(name));

// =============================================================================
//  WRITERS — escriben a localStorage según el contrato de cada feature
// =============================================================================

// content_uploads (barber feed/profile/portfolio/before_after)
const writeContentUpload = ({ user, media, fields, contentType, status = 'pending_approval' }) => {
  const arr = JSON.parse(localStorage.getItem('content_uploads') || '[]');
  const entry = {
    id: `upload_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    barberId: user.id,
    barberUsername: user.username,
    barberName: user.name,
    barberAvatar: user.avatar,
    contentType,                    // 'feed_post' | 'portfolio_photo' | 'before_after' | 'reel' | ...
    category: fields.category || 'Freestyle',
    description: fields.description || '',
    location: fields.location || '',
    tags: fields.tags || [],
    relatedBattle: fields.relatedBattle || '',
    relatedTournament: fields.relatedTournament || '',
    relatedChallenge: fields.relatedChallenge || '',
    visibility: fields.visibility || 'public',
    title: fields.title || '',
    // media payload — usa los campos canon del schema legado
    photoUrl: media?.type === 'image' ? media.dataUrl : '',
    videoUrl: media?.type === 'video' ? media.dataUrl : '',
    thumbnailUrl: media?.thumbnailDataUrl || (media?.type === 'image' ? media.dataUrl : ''),
    durationSec: media?.durationSec || null,
    width: media?.width || null,
    height: media?.height || null,
    sizeMB: media?.sizeMB || 0,
    cropAspect: media?.cropAspect || null,
    quality: media?.quality || null,
    status,
    createdAt: new Date().toISOString(),
    approvedAt: null,
    rejectedAt: null,
    adminNotes: ''
  };
  arr.push(entry);
  localStorage.setItem('content_uploads', JSON.stringify(arr));
  event('lineage_uploads_updated');
  return entry;
};

// lineage_admin_content (admin news/ads/banners) — usa el helper existente
const writeAdminContent = ({ media, fields }) => {
  const arr = JSON.parse(localStorage.getItem('lineage_admin_content') || '[]');
  const entry = {
    id: `ac_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    title: fields.title || tr("Sin título"),
    description: fields.description || '',
    type: fields.contentType || 'news',
    priority: fields.priority || 'medium',
    status: fields.status || 'active',
    audienceRoles: fields.audienceRoles || ['barber', 'client', 'brand'],
    locations: fields.locations || ['feed_main'],
    isSponsored: !!fields.isSponsored,
    brandName: fields.brandName || '',
    mediaUrl: media?.dataUrl || '',
    mediaType: media?.type || 'image',
    thumbnailUrl: media?.thumbnailDataUrl || (media?.type === 'image' ? media?.dataUrl : ''),
    cropAspect: media?.cropAspect || null,
    quality: media?.quality || null,
    width: media?.width || null,
    height: media?.height || null,
    ctaLabel: fields.ctaLabel || '',
    ctaLink: fields.ctaLink || '',
    startDate: fields.startDate || null,
    endDate: fields.endDate || null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    metrics: { impressions: 0, clicks: 0, conversions: 0 }
  };
  arr.unshift(entry);
  localStorage.setItem('lineage_admin_content', JSON.stringify(arr));
  event('lineage_admin_content_updated');
  return entry;
};

// lineage_brand_posts (brand orgánico — feed propio de marca, NO publicidad oficial)
const writeBrandPost = ({ user, media, fields }) => {
  const arr = JSON.parse(localStorage.getItem('lineage_brand_posts') || '[]');
  const entry = {
    id: `bp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    brandId: user.id,
    brandUsername: user.username,
    brandName: user.name,
    brandLogo: user.avatar,
    title: fields.title || '',
    description: fields.description || '',
    tags: fields.tags || [],
    visibility: fields.visibility || 'public',
    photoUrl: media?.type === 'image' ? media.dataUrl : '',
    videoUrl: media?.type === 'video' ? media.dataUrl : '',
    thumbnailUrl: media?.thumbnailDataUrl || '',
    cropAspect: media?.cropAspect || null,
    quality: media?.quality || null,
    width: media?.width || null,
    height: media?.height || null,
    status: 'approved',                 // posts orgánicos no requieren approval
    createdAt: new Date().toISOString()
  };
  arr.unshift(entry);
  localStorage.setItem('lineage_brand_posts', JSON.stringify(arr));
  event('lineage_brand_posts_updated');
  return entry;
};

// lineage_client_posts (cliente — reseñas, comentarios visuales, avatar)
const writeClientPost = ({ user, media, fields, kind = 'review' }) => {
  const arr = JSON.parse(localStorage.getItem('lineage_client_posts') || '[]');
  const entry = {
    id: `cp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    clientId: user.id,
    clientUsername: user.username,
    clientName: user.name,
    clientAvatar: user.avatar,
    kind,                              // 'review' | 'avatar' | 'comment_visual'
    description: fields.description || '',
    rating: fields.rating || null,
    barberUsername: fields.barberUsername || '',
    photoUrl: media?.type === 'image' ? media.dataUrl : '',
    videoUrl: media?.type === 'video' ? media.dataUrl : '',
    thumbnailUrl: media?.thumbnailDataUrl || '',
    status: 'approved',
    createdAt: new Date().toISOString()
  };
  arr.unshift(entry);
  localStorage.setItem('lineage_client_posts', JSON.stringify(arr));
  event('lineage_client_posts_updated');
  return entry;
};

// =============================================================================
//  DESTINOS — declarativos, agrupados por rol
// =============================================================================

export const DESTINATIONS = {
  // ========== BARBER ==========
  feed_post: {
    id: 'feed_post', label: tr("Feed (post social)"), role: 'barber',
    description: tr("Aparece en el Ziyup Feed y en tu perfil. Recibe likes/comentarios."),
    requiresApproval: true, mediaAccept: 'both',
    write: ({ user, media, fields }) =>
      writeContentUpload({ user, media, fields, contentType: 'feed_post' })
  },
  portfolio_photo: {
    id: 'portfolio_photo', label: 'Portafolio',  role: 'barber',
    description: tr("Foto de tu portafolio profesional (perfil público)."),
    requiresApproval: true, mediaAccept: 'image',
    write: ({ user, media, fields }) =>
      writeContentUpload({ user, media, fields, contentType: 'portfolio_photo' })
  },
  before_after: {
    id: 'before_after', label: tr("Antes y Después"), role: 'barber',
    description: tr("Transformación cliente — se muestra como par antes/después."),
    requiresApproval: true, mediaAccept: 'image',
    write: ({ user, media, fields }) =>
      writeContentUpload({ user, media, fields, contentType: 'before_after' })
  },
  reel: {
    id: 'reel', label: tr("Reel / Video corto"), role: 'barber',
    description: tr("Video vertical para el feed estilo TikTok."),
    requiresApproval: true, mediaAccept: 'video',
    write: ({ user, media, fields }) =>
      writeContentUpload({ user, media, fields, contentType: 'highlight_video' })
  },
  battle_content: {
    id: 'battle_content', label: tr("Contenido de batalla 1vs1"), role: 'barber',
    description: tr("Asociar foto/video a una batalla 1vs1."),
    requiresApproval: true, mediaAccept: 'both',
    write: ({ user, media, fields }) =>
      writeContentUpload({ user, media, fields, contentType: 'battle_content' })
  },
  tournament_content: {
    id: 'tournament_content', label: tr("Contenido de torneo"), role: 'barber',
    description: tr("Asociar foto/video a un torneo."),
    requiresApproval: true, mediaAccept: 'both',
    write: ({ user, media, fields }) =>
      writeContentUpload({ user, media, fields, contentType: 'tournament_content' })
  },

  // ========== BRAND ==========
  brand_post: {
    id: 'brand_post', label: tr("Feed de marca (orgánico)"), role: 'brand',
    description: tr("Post orgánico de tu marca — aparece en tu feed sin aprobación."),
    requiresApproval: false, mediaAccept: 'both',
    write: ({ user, media, fields }) => writeBrandPost({ user, media, fields })
  },

  // ========== CLIENT ==========
  client_review: {
    id: 'client_review', label: tr("Reseña con foto/video"), role: 'client',
    description: tr("Reseña visual de un servicio con tu barbero."),
    requiresApproval: false, mediaAccept: 'both',
    write: ({ user, media, fields }) =>
      writeClientPost({ user, media, fields, kind: 'review' })
  },
  client_avatar: {
    id: 'client_avatar', label: tr("Foto de perfil"), role: 'client',
    description: tr("Cambia tu avatar — solo imagen."),
    requiresApproval: false, mediaAccept: 'image',
    write: ({ user, media }) => {
      // Actualiza avatar en lineage_user + lineage_users
      const cur = JSON.parse(localStorage.getItem('lineage_user') || 'null');
      if (cur) {
        cur.avatar = media?.dataUrl || cur.avatar;
        localStorage.setItem('lineage_user', JSON.stringify(cur));
      }
      const reg = JSON.parse(localStorage.getItem('lineage_users') || '{}');
      if (reg[user.username]) {
        reg[user.username].avatar = media?.dataUrl || reg[user.username].avatar;
        localStorage.setItem('lineage_users', JSON.stringify(reg));
      }
      event('lineage_user_updated');
      return { id: 'avatar', avatarUrl: media?.dataUrl };
    }
  },

  // ========== ADMIN ==========
  admin_news:        { id: 'admin_news',        label: 'Noticia oficial',           role: 'admin', mediaAccept: 'image', write: ({ media, fields }) => writeAdminContent({ media, fields: { ...fields, contentType: 'news' } }) },
  admin_announcement:{ id: 'admin_announcement',label: 'Comunicado',                role: 'admin', mediaAccept: 'both',  write: ({ media, fields }) => writeAdminContent({ media, fields: { ...fields, contentType: 'announcement' } }) },
  admin_banner:      { id: 'admin_banner',      label: tr("Banner principal"),          role: 'admin', mediaAccept: 'image', write: ({ media, fields }) => writeAdminContent({ media, fields: { ...fields, contentType: 'banner_main' } }) },
  admin_ad:          { id: 'admin_ad',          label: tr("Publicidad interna"),        role: 'admin', mediaAccept: 'both',  write: ({ media, fields }) => writeAdminContent({ media, fields: { ...fields, contentType: 'ad_internal' } }) },
  admin_promo:       { id: 'admin_promo',       label: tr("Promoción"),                 role: 'admin', mediaAccept: 'both',  write: ({ media, fields }) => writeAdminContent({ media, fields: { ...fields, contentType: 'promo_tournament' } }) },
  admin_video_feat:  { id: 'admin_video_feat',  label: tr("Video destacado"),           role: 'admin', mediaAccept: 'video', write: ({ media, fields }) => writeAdminContent({ media, fields: { ...fields, contentType: 'video_featured' } }) }
};

// Devuelve la lista de destinos disponibles para un rol dado.
export const getDestinationsForRole = (role) => {
  if (!role) return [];
  return Object.values(DESTINATIONS).filter((d) => d.role === role);
};

// Helper para escribir directamente
export const publishToDestination = (destinationId, payload) => {
  const dest = DESTINATIONS[destinationId];
  if (!dest) throw new Error(tr("Destino desconocido: {0}", destinationId));
  return dest.write(payload);
};
